/** Mentions légales affichées : 1 ou 2 lignes, virgules, jamais de tiret cadratin. */

export type LegalCompany = {
  legal_form?: string | null;
  share_capital?: string | null;
  rcs_city?: string | null;
  siren?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  late_penalty_label?: string | null;
  early_discount_bp?: number | null;
  vat_regime?: string | null;
};

function fmtSiren(raw: string): string {
  const d = raw.replace(/\D+/g, "");
  return d.length === 9 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)}` : d;
}

export function legalFormIsSociete(form: string): boolean {
  return /\b(SASU|SAS|SARL|EURL|SA|SCI|SNC|SCA|SCS|SELARL|SELAS|SELAFA|SELCA)\b/i.test(form);
}

export function identityLine(company: LegalCompany): string {
  const bits: string[] = [];
  const form = (company.legal_form || "").trim();
  const cap = (company.share_capital || "").trim();
  const head = [form, cap ? `au capital de ${cap}` : ""].filter(Boolean).join(" ");
  if (head) bits.push(head);
  const rcs = (company.rcs_city || "").trim();
  if (rcs) bits.push(`RCS ${rcs}`);
  const siren = (company.siren || "").replace(/\D+/g, "");
  if (siren.length === 9) bits.push(`SIREN ${fmtSiren(siren)}`);
  return bits.join(", ");
}

export function footerLines(company: LegalCompany, siretFmt = ""): string[] {
  const line1 = identityLine(company);
  const ids: string[] = [];
  if (siretFmt) ids.push(`SIRET ${siretFmt}`);
  const vat = (company.vat_number || "").trim();
  if (vat) ids.push(`TVA ${vat}`);
  ids.push("Conservation des factures : 10 ans.");
  return [line1, ids.join(", ")].filter(Boolean);
}

function discountSentence(company: LegalCompany): string {
  const bp = Number(company.early_discount_bp || 0);
  if (bp <= 0) return "Aucun escompte pour paiement anticipé.";
  const pct = (bp / 100).toLocaleString("fr-FR");
  return `Escompte de ${pct} % pour paiement anticipé.`;
}

export function termSentences(company: LegalCompany, kind: "invoice" | "quote" | "credit_note", duePhrase: string): string[] {
  const lines: string[] = [];
  if ((company.vat_regime || "") === "franchise") {
    lines.push("TVA non applicable, art. 293 B du CGI.");
  }
  if (kind === "quote") {
    if (duePhrase) lines.push(`Offre valable jusqu’au ${duePhrase}.`);
    return lines;
  }
  if (duePhrase) lines.push(`Paiement : ${duePhrase}.`);
  lines.push(discountSentence(company));
  const rate = (company.late_penalty_label || "").trim() || "trois fois le taux d’intérêt légal";
  lines.push(
    `En cas de retard de paiement, pénalités calculées sur la base de ${rate} et indemnité forfaitaire de 40 € pour frais de recouvrement (art. L. 441-10 du code de commerce).`,
  );
  return lines;
}

export function sentencesToHtml(lines: string[]): string {
  return lines
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => `<p>${s.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p>`)
    .join("");
}

function looksAuto(html: string): boolean {
  const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  if (!plain) return true;
  return [
    "aucun escompte pour paiement anticipé",
    "indemnité forfaitaire de 40",
    "conservation des factures",
    "au capital de",
  ].some((n) => plain.includes(n));
}

export function mergeCustom(autoHtml: string, customHtml: string | null | undefined): string {
  const extra = (customHtml || "").trim();
  if (!extra || looksAuto(extra)) return autoHtml;
  return autoHtml + extra;
}
